import { useState } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CloseIcon from '@material-ui/icons/Close';
import MenuIcon from '@material-ui/icons/Menu';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import clsx from 'clsx';
import { useIsAuthenticated, useLogout } from '@hilma/auth';
import { useDirection, useSwitchedPath, LanguageNavLink, LanguageLink } from '@hilma/netina2all';

import { useI18n, useTranslate, useCustomer } from '../../hooks';
import { routes } from '../../constants';

interface TopbarProps {
	currentPath: string;
}

const TopbarMobile: React.FC<TopbarProps> = ({ currentPath }) => {
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [anchorElement, setAnchorElement] = useState<HTMLImageElement | null>(null);

	const classes = useStyles();

	const customer = useCustomer();

	const switchedPath = useSwitchedPath();
	const translate = useTranslate();
	const direction = useDirection();

	const alts = useI18n(i18n => i18n.alts);
	const topbar = useI18n(i18n => i18n.topbar);
	const lo = useI18n(i18n => i18n.sideBar.lo);

	const isAuthenticated = useIsAuthenticated();
	const logout = useLogout();

	return (
		<>
			<MenuIcon onClick={() => setIsSidebarOpen(true)} titleAccess={alts.showMenu} />
			<Drawer
				classes={{ paper: classes.drawerPaper }}
				anchor={'left'}
				open={isSidebarOpen}
				onClose={() => setIsSidebarOpen(false)}
			>
				<CloseIcon className={classes.close + ' pointer'} onClick={() => setIsSidebarOpen(false)} titleAccess={alts.closeMenu} />
				{routes.map(([path, name]) => (
					<LanguageNavLink
						key={path}
						to={path}
						activeClassName={path === "/" ? (currentPath === "" ? "current" : "") : "current"}
						onClick={() => setIsSidebarOpen(false)}
					>
						<div style={{ direction }}>
							{translate(name)}
						</div>
					</LanguageNavLink>
				))}
				<Link to={switchedPath}>{topbar.otherLang}</Link>
			</Drawer>
			<LanguageLink to='/'>
				<img className='logo' src='/lulav-icons/logo.svg' alt={alts.latetLogo} />
			</LanguageLink>

			{currentPath.includes('profile') && isAuthenticated ? (
				<>
					<img
						ref={setAnchorElement}
						src="/lulav-icons/profile.svg"
						alt={alts.profileIcon}
						onClick={() => setIsMenuOpen(true)}
					/>
					<Menu
						open={isMenuOpen}
						anchorEl={anchorElement}
						onClose={() => setIsMenuOpen(false)}
						classes={{ list: classes.menuList }}
					>
						<MenuItem
							button={false}
							className={clsx(classes.menuItem, classes.name)}
						>
							{customer?.fullName}
						</MenuItem>
						<Divider className={classes.divider} />
						<MenuItem
							className={clsx(classes.menuItem, classes.logout)}
							onClick={() => (setIsMenuOpen(false), logout())}
						>
							<ExitToAppIcon className={classes.icon} />
							<div>{lo}</div>
						</MenuItem>
					</Menu>
				</>
			) : (
				<LanguageLink to='/profile'>
					<img src='/lulav-icons/profile.svg' alt={alts.profileIcon} />
				</LanguageLink>
			)}
		</>
	);
}

const useStyles = makeStyles((theme) => ({
	drawerPaper: {
		width: '50vw',
		backgroundColor: theme.palette.primary.main,
		'& a, & span': {
			color: 'white',
			textDecoration: 'none',
			padding: '15px calc(5vw + 5px)',
			fontWeight: 600,
			fontSize: '1.2rem'
		},
		'& .current': {
			backgroundColor: '#ffffff24'
		}
	},

	close: {
		fill: 'white',
		margin: '20px 5vw 15px',
	},

	menuList: {
		padding: '3.5px 0'
	},

	menuItem: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 0,
		padding: '8.5px 11px',
		outline: 'none',
		'&, *': {
			color: theme.palette.primary.main,
			fontSize: 15
		}
	},

	name: {
		fontFamily: 'Assistant-Bold'
	},

	logout: {
		fontFamily: 'Assistant-SemiBold'
	},

	divider: {
		margin: '0 11px'
	},

	icon: {
		width: 15,
		height: 15,
		marginRight: 4
	}
}))

export default TopbarMobile;